import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useBoolean } from 'react-hanger';
import { useLocation } from 'react-router';
import { useMutation } from '@apollo/client/react';
import { CompositeFilterDescriptor, DataResult, process, SortDescriptor } from '@progress/kendo-data-query';
import { ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';
import {
    GridColumnMenuFilter,
    GridColumnMenuProps,
    GridColumnReorderEvent,
    GridFilterChangeEvent,
    GridPageChangeEvent,
    GridSortChangeEvent,
    GridColumnProps as KendoGridColumnProps,
    GridColumnChildrenProps,
} from '@progress/kendo-react-grid';
import cn from 'clsx';
import { cloneDeep } from 'lodash';
import { DateFilter, BooleanFilter } from './custom-filter';
import CoreGrid, { CoreGridHandle } from '@components-clean/core/grid';
import CardGrid from '@components-clean/core/grid/card-grid';
import { fieldObjectCompare } from '@components-clean/core/grid/grid.helper';
import { GridColumnProps } from '@components-clean/core/grid/grid.interface';
import ExportExcel from '@components-clean/core/grid-fe/export-excel';
import { FeGridProps } from '@components-clean/core/grid-fe/grid.interface';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { useLocalState } from '@hooks/index';
import NotificationService from '@utils/notification';
import { ADD_EXPORT_FE_GRID_DATA_AUDIT } from '@queries/audit';
import { requireStudy } from '@config/routes/page-data-helpers';

const ColumnMenuFilterOnly = (type: string | undefined) =>
    function CustomFilterMenu(props: GridColumnMenuProps) {
        const filterUi = () => {
            switch (type) {
                case 'date':
                    return DateFilter;
                case 'boolean':
                    return BooleanFilter;
                default:
                    return undefined;
            }
        };
        return <GridColumnMenuFilter {...props} expanded filterUI={filterUi()} />;
    };

const Grid = ({
    columns,
    className,
    data,
    style,
    gridViewOptions,
    exportFilename,
    exportCallback,
    clearFilter,
    toggleReorder,
    saveReorder,
    rowReorder,
    addItemDialog,
    addCallback,
    onTitleChange,
    gridSettingsMenuDisabled,
    filter,
    filterListener,
    showPlaceholder,
    onBulkUpdateClicked,
    clearSelectedRows,
    clearGridSelectedRows,
    useCardWrapper = true,
    resetChanges,
    setEditMode,
    onFilterChange,
    preventAutoscroll,
    customGridMenuItems,
    removeCardBorder,
    gridDownloadDisabled,
    service,
    area,
    dataType,
    auditDescriptionPrefix,
    selectionDisabledField,
    scrollable,
    excludeHiddenRowMenuActions,
    persistentStorageKey,
    ...rest
}: FeGridProps) => {
    const filterableColumns = (input: GridColumnProps[]): GridColumnProps[] => {
        return input.map((column, index) => {
            return !!column.children
                ? {
                      ...column,
                      children: filterableColumns(column.children || []),
                      orderIndex: index,
                  }
                : {
                      ...column,
                      columnMenu: ColumnMenuFilterOnly(column.filter),
                      orderIndex: index,
                  };
        });
    };
    const { confirmDiscardChanges, anyComponentHasChanges } = useUnsavedChangesContext();

    const organisedColumns = filterableColumns(columns);

    const clearSelectedRowsEvent = useBoolean(false);

    const [exportTrigger, setExportTrigger] = useState<number>(0);
    const [filterState, setFilterState] = useState<CompositeFilterDescriptor | undefined>({
        logic: 'and',
        filters: [],
    });
    const [sortState, setSortState] = useState<SortDescriptor[]>([]);
    const [orderedColumns, setColumnOrder] = useState(organisedColumns);
    // Owned here (rather than left to CardGrid's own internal fallback) so applying a saved layout
    // via GridStateMenu — which bumps gridKey — also resets our own live-tracked column order below.
    // Without this, our onColumnReorder-driven columnOrder state never learns a layout was reset.
    const [gridKey, setGridKey] = useState(0);
    const [processedData, setProcessedData] = useState<unknown[]>(
        process((data || []) as unknown[], { filter: filterState, sort: sortState }).data,
    );

    const localState = useLocalState();
    const location = useLocation();
    const [addExportFeGridDataAudit] = useMutation(ADD_EXPORT_FE_GRID_DATA_AUDIT);
    const [showAddDialog, setShowAddDialog] = useState<boolean>(false);

    const coreGridRef = useRef<CoreGridHandle | null>(null);
    const [skip, setSkip] = useState(0);
    const exportToExcelClicked = useRef(false);
    const updateFilters = (newFilter?: CompositeFilterDescriptor) => {
        // Any filter change replaces/shrinks the result set. Reset the virtual-scroll offset so Kendo
        // doesn't render an out-of-range (blank) viewport when the new result set is smaller than the
        // current scroll position. This covers every filter entry point (column menu, external filter
        // prop / filter-ui, clear, reorder, title change) since they all funnel through here.
        if (scrollable === 'virtual') setSkip(0);

        if (newFilter?.filters) {
            clearSelectedRowsEvent.setTrue();
        } else {
            clearSelectedRowsEvent.setFalse();
        }

        setFilterState(newFilter);
        coreGridRef.current?.clearSelectedRows();
        filterListener && filterListener(newFilter);
    };

    useEffect(() => {
        if (JSON.stringify(filter) !== JSON.stringify(filterState)) updateFilters(filter);
    }, [filter]);

    useEffect(() => {
        if (clearGridSelectedRows !== undefined) clearSelectedRowsEvent.setValue(clearGridSelectedRows);
    }, [clearGridSelectedRows]);

    const updateSortState = (newSortState: SortDescriptor[] | undefined) => {
        setSortState((prevSortState) => {
            if (JSON.stringify(prevSortState) === JSON.stringify(newSortState)) {
                return prevSortState;
            }
            return newSortState || [];
        });
    };

    const handleOnTitleChange = (e: ComboBoxChangeEvent) => {
        onTitleChange?.(e);
        updateFilters(undefined);
        updateSortState([]);
        setColumnOrder(organisedColumns);
    };

    useEffect(() => {
        // Only re-sync column order to the base config on a gridKey bump when persistent storage is on.
        // gridKey is bumped for several reasons (locking columns, window resize, clearing filters) but the
        // reason this reset exists is applying a saved layout via GridStateMenu — which is only rendered
        // when persistentStorageKey is set. In that persistent case the live lock/hide/order state is
        // re-applied from the saved columnsState immediately after this reset, so nothing is lost. Without
        // a persistentStorageKey there is no columnsState to restore from, so resetting here would wipe the
        // user's in-session lock/hide changes (e.g. locking a column would silently revert it and un-hide
        // any previously hidden columns), so skip the reset entirely in that mode.
        if (persistentStorageKey) setColumnOrder(organisedColumns);
    }, [gridKey]);

    useEffect(() => {
        const newData = process((data || []) as object[], { filter: filterState, sort: sortState }).data;
        setProcessedData(newData);
        // Defense-in-depth: if the (re)computed result set is now smaller than the current virtual-scroll
        // offset (e.g. an external data refetch returned fewer rows), reset the offset to avoid Kendo
        // rendering a blank viewport beyond the available rows. Functional updater avoids a `skip` dep.
        if (scrollable === 'virtual') setSkip((prev) => (prev >= newData.length ? 0 : prev));
        coreGridRef.current?.validateSelection(newData);
    }, [data, filterState, sortState]);

    const flattenGroupedData = (dataToFlatten: unknown[]): object[] => {
        return dataToFlatten.flatMap((x) => {
            const item = x as { items: unknown[] };
            return item?.items !== undefined ? flattenGroupedData(item?.items) : x instanceof Object ? [x] : [{ x }];
        });
    };

    const fullCallback = () => {
        exportToExcelClicked.current = true;

        // If filename, service, area, and dataType aren't all defined, just do the export without creating an audit log
        if (exportFilename == null || service == null || area == null || dataType == null) {
            exportCallback && exportCallback();
            setExportTrigger(exportTrigger + 1);
            return;
        }

        // Otherwise, attempt to audit the export, and only allow if successful
        // Not all screens require a study id, for those that don't, use an empty object id
        // For those that do and no study id is found, return
        const requireStudyId = requireStudy(location.pathname);
        let studyId = localState.state?.selectedStudy?.id;
        if (requireStudyId && !studyId) {
            return;
        }
        if (!requireStudyId) studyId = '000000000000000000000000';

        addExportFeGridDataAudit({
            variables: {
                studyId,
                filename: auditDescriptionPrefix ? `${auditDescriptionPrefix} - ${exportFilename}` : exportFilename,
                service,
                area,
                dataType,
            },
        })
            .then((res) => {
                if (res.data?.addExportFEGridDataAudit?.success) {
                    exportCallback && exportCallback();
                    setExportTrigger(exportTrigger + 1);
                } else {
                    NotificationService.showNotification('Something went wrong');
                }
            })
            .catch(() => {
                NotificationService.showNotification('Something went wrong');
            });
    };

    const compareColumnsByOrderIndex = (column1: GridColumnProps, column2: GridColumnProps) =>
        (column1?.orderIndex || 0) - (column2?.orderIndex || 0);

    // Call the users save confirmation and update the data state here. If either fails, return false so the core grid
    // knows it failed.
    const fullSaveReorder = (newOrderedData: unknown[] | DataResult | null | undefined) => {
        const customCall = !saveReorder || saveReorder(newOrderedData);
        if (!customCall) return false;
        if (Array.isArray(newOrderedData)) {
            setProcessedData(newOrderedData);
            return true;
        }
        if (newOrderedData && newOrderedData.data) {
            setProcessedData(newOrderedData.data);
            return customCall ?? true;
        }
        return false;
    };

    const clearGridState = (clearFilters: boolean, clearSort: boolean) => {
        if (clearFilters) updateFilters(undefined);
        if (clearSort) updateSortState([]);
    };

    // Holds the full column state in the core grid for export purposes
    const [exportColumns, setExportColumns] = useState<GridColumnProps[]>(orderedColumns);

    const memoizedSetExportColumns = useCallback((newCols: GridColumnProps[]) => {
        setExportColumns((prevCols) => {
            if (JSON.stringify(prevCols) === JSON.stringify(newCols)) {
                return prevCols; // Avoid unnecessary updates
            }
            return newCols;
        });
    }, []);

    // Recursively search columns for column with a matching field
    const searchColumns = (cols: GridColumnProps[], field: string): GridColumnProps | undefined => {
        for (let i = 0; i < cols.length; i++) {
            const col = cols[i];
            if (col.field === field) return col;
            if (col.children && col.children.length > 0) {
                const foundCol = searchColumns(col.children, field);
                if (foundCol) return foundCol;
            }
        }
    };

    const gridComponentProps = {
        ref: coreGridRef,
        showPlaceholder,
        onColumnReorder: (e: GridColumnReorderEvent) => {
            const mapColumnAndChildren = (
                col: GridColumnProps,
                eventCols: KendoGridColumnProps[] | GridColumnChildrenProps[],
            ): GridColumnProps => {
                const match = eventCols.find((userCol) => userCol.field === col.field);

                return match
                    ? ({
                          ...col,
                          children: col.children?.map((childCol) =>
                              mapColumnAndChildren(childCol, match.children || []),
                          ),
                          id: match.id,
                          orderIndex: match.orderIndex,
                          show: true,
                          locked: match.locked,
                      } as GridColumnProps)
                    : { ...col, show: false }; // No match means the column is hidden
            };

            setColumnOrder((state) => {
                return state.map((col) => {
                    return mapColumnAndChildren(col, e.columns);
                });
            });
        },
        gridViewOptions,
        onFilterChange: async (e: GridFilterChangeEvent) => {
            if (onFilterChange) {
                const proceed = await onFilterChange(e);
                if (proceed === false) return;
            }
            // updateFilters resets the virtual-scroll offset for all filter paths.
            updateFilters(e.filter);
        },
        clearFilter: async () => {
            // Call any provided additional logic as well as clearing the filters
            if (clearFilter) {
                const proceed = await clearFilter();
                if (proceed === false) return false;
            }
            updateFilters(undefined);
        },
        onSortChange: (e: GridSortChangeEvent) => {
            if (e.sort) {
                e.sort = e.sort.map((sort) => {
                    let filterKey = organisedColumns.find((col) => col.field === sort.field)?.filterKey;
                    if (!filterKey) {
                        filterKey = organisedColumns
                            .find((col) => col.id === sort.field.split('.')[0])
                            ?.children?.find((col) => col.field === sort.field)?.filterKey;
                    }

                    // This is a catch-all where the parent/child column id/field doesn't match the above pattern,
                    //  specially for Multi-Value, as I'm now 100% sure on the scope of the impact
                    if (!filterKey) {
                        const col = searchColumns(organisedColumns, sort.field);
                        if (col && col.multiValueTypeColumn) filterKey = col.filterKey;
                    }

                    if (filterKey) {
                        return {
                            ...sort,
                            compare: (a: unknown, b: unknown) =>
                                fieldObjectCompare(
                                    sort.field,
                                    filterKey,
                                    a as {
                                        [key: string]: { [key: string]: string | number | null | undefined };
                                    },
                                    b as {
                                        [key: string]: { [key: string]: string | number | null | undefined };
                                    },
                                    sort.dir || 'asc',
                                ),
                        };
                    }
                    return sort;
                });
            }
            // Changing sort causes the grid to jump back to the top of its scroll position
            // but this was not resetting skip which can cause rows to not render
            if (scrollable === 'virtual') setSkip(0);

            setSortState(e.sort);
        },
        sort: sortState,
        filter: filterState,
        columns: orderedColumns,
        data: processedData,
        exportCallback: fullCallback,
        rowReorder,
        saveReorder: fullSaveReorder,
        toggleReorder: (isActive: boolean) => {
            if (sortState.length > 0 || filterState)
                if (isActive) {
                    updateSortState([]);
                    updateFilters(undefined);
                    return (!toggleReorder || toggleReorder(isActive)) && true;
                }
            return false;
        },
        addCallback: async () => {
            if (anyComponentHasChanges()) {
                const confirmed = await confirmDiscardChanges();
                if (!confirmed) {
                    return;
                }
                if (resetChanges) resetChanges();
                if (setEditMode) setEditMode();
                if (addItemDialog) setShowAddDialog(true);
                if (addCallback) addCallback();
            } else if (addItemDialog) {
                if (setEditMode) setEditMode();
                setShowAddDialog(true);
            } else {
                addCallback && addCallback();
            }
        },
        onTitleChange: handleOnTitleChange,
        setExportColumns: memoizedSetExportColumns,
        ...rest,
        clientGrid: true,
        gridSettingsMenuDisabled,
        gridDownloadDisabled,
        noPadding: true,
        onBulkUpdateClicked,
        clearGridSelectedRows: clearSelectedRowsEvent.value,
        preventAutoscroll,
        customGridMenuItems,
        removeCardBorder,
        selectionDisabledField,
        onPageChange: (event: GridPageChangeEvent) => {
            if (scrollable === 'virtual') {
                setSkip(event.page.skip);
            }
            rest.onPageChange && rest.onPageChange(event);
        },
        skip: scrollable === 'virtual' ? skip : undefined,
        scrollable,
        excludeHiddenRowMenuActions,
        persistentStorageKey,
        gridKey,
        setGridKey,
    };

    useEffect(() => {
        if (organisedColumns.length !== gridComponentProps.columns.length) {
            setColumnOrder(organisedColumns);
        }
    }, [columns, orderedColumns]);

    return (
        <div className={cn('grid-fe-component k-h-full', className)}>
            {exportToExcelClicked.current && (
                <div className="k-hidden">
                    <ExportExcel
                        exportTrigger={exportTrigger}
                        data={flattenGroupedData(cloneDeep(processedData))}
                        columns={exportColumns.toSorted(compareColumnsByOrderIndex)}
                        fileName={exportFilename ?? ''}
                        mapColumns={(col) => col}
                        onExportComplete={() => {
                            exportToExcelClicked.current = false;
                            setExportTrigger(0);
                        }}
                    />
                </div>
            )}
            {showAddDialog &&
                addItemDialog &&
                addItemDialog(
                    (clearFilters, clearSort) => {
                        clearGridState(clearFilters, clearSort);
                        setShowAddDialog(false);
                    },
                    () => setShowAddDialog(false),
                )}
            {useCardWrapper ? <CardGrid {...gridComponentProps} /> : <CoreGrid {...gridComponentProps} />}
        </div>
    );
};

export default Grid;
